import React, { useEffect, useMemo, useState } from "react";
import Delete from "@mui/icons-material/Delete";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useDeleteInboxMessage, useInboxMessage, usePublisherGameAsDesigner } from "@seabrookstudios/pitch2table";
import { formatFullDate, isUnverifiedInboxEmail, PublisherGameStatusLabel } from "@seabrookstudios/pitch2table-core";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { EmptyDetailCard } from "../core/EmptyDetailCard";
import { Path } from "../Path";
import { FileInboxMessageDialog } from "./FileInboxMessageDialog";
import { InboxRelatedDestinations } from "./InboxRelatedDestinations";
import { RemoteInboxImages } from "./RemoteInboxImages";
import { UnverifiedSenderWarning } from "./UnverifiedSenderWarning";
import { useInboxMessageIdFromRoute } from "./WithInboxMessageIdFromRoute";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherGameId} props.publisherGameId
 */
const FiledToSummary = ({ publisherGameId }) => {
  const navigate = useNavigate();
  const { data: publisherGame } = usePublisherGameAsDesigner(publisherGameId);

  if (!publisherGame) {
    return <Typography variant="body2">Filed to submission</Typography>;
  }

  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">
        Filed to {publisherGame.publisherName} ({PublisherGameStatusLabel[publisherGame.status] || publisherGame.status}
        )
      </Typography>
      <Button
        size="small"
        variant="text"
        sx={{ alignSelf: "flex-start", px: 0 }}
        onClick={() => navigate(Path.game(publisherGame.gameId).publisher(publisherGame.publisherId))}
      >
        Open submission
      </Button>
    </Stack>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").InboundEmail} props.message
 */
const InboxMessageBody = ({ message }) => {
  const blocks = useMemo(() => {
    if (Array.isArray(message.textBlocks) && message.textBlocks.length > 0) {
      return message.textBlocks;
    }
    return message.text ? [message.text] : [];
  }, [message.text, message.textBlocks]);

  const initialIndex =
    typeof message.textBlockIndex === "number" && message.textBlockIndex >= 0 && message.textBlockIndex < blocks.length
      ? message.textBlockIndex
      : 0;

  const [blockIndex, setBlockIndex] = useState(initialIndex);

  // Reset when switching messages
  useEffect(() => {
    setBlockIndex(initialIndex);
  }, [message.id, initialIndex]);

  const body = blocks[blockIndex] || "(no body text)";
  const canShowOlder = blockIndex < blocks.length - 1;
  const canShowNewer = blockIndex > 0;

  return (
    <Stack spacing={1}>
      {blocks.length > 1 && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" color="text.secondary">
            Thread part {blockIndex + 1} of {blocks.length}
          </Typography>
          <Button size="small" disabled={!canShowNewer} onClick={() => setBlockIndex((i) => Math.max(0, i - 1))}>
            Newer
          </Button>
          <Button
            size="small"
            disabled={!canShowOlder}
            onClick={() => setBlockIndex((i) => Math.min(blocks.length - 1, i + 1))}
          >
            Older
          </Button>
        </Stack>
      )}
      <Box sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        <Typography variant="body1">{body}</Typography>
      </Box>
      <RemoteInboxImages
        key={`${message.id}-${blockIndex}`}
        text={body}
        enabled={isUnverifiedInboxEmail(message)}
      />
    </Stack>
  );
};

export const InboxMessageDetailFromRoute = () => {
  const messageId = useInboxMessageIdFromRoute();
  const navigate = useNavigate();
  const { data: message, isLoading, isError } = useInboxMessage(messageId || null);

  const deleteMessage = useDeleteInboxMessage(
    () => {
      enqueueSnackbar("Email deleted", { variant: "success" });
      navigate(Path.inbox().none());
    },
    () => enqueueSnackbar("Could not delete email", { variant: "error" })
  );

  if (!messageId) {
    return <EmptyDetailCard text="Select an email from the list on the left." />;
  }

  if (isLoading) {
    return (
      <Card sx={{ height: "85vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Card>
    );
  }

  if (isError || !message) {
    return <EmptyDetailCard text="Email not found or it has expired." />;
  }

  const from = message.from || message.originalFrom || "Unknown sender";
  const isOpen = message.classification === "notDone";

  const onDelete = () => {
    if (!window.confirm("Delete this email from your inbox?")) {
      return;
    }
    deleteMessage.mutate(message.id);
  };

  return (
    <>
      <InboxRelatedDestinations message={message} />
      <Card sx={{ height: "85vh", overflow: "auto" }}>
        <CardHeader
          title={message.subject || "(no subject)"}
          subheader={
            <Stack spacing={0.25}>
              <Typography variant="body2" color="text.secondary">
                From {from}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatFullDate(message.receivedAt)}
              </Typography>
            </Stack>
          }
          action={
            isOpen ? (
              <IconButton aria-label="Delete email" color="error" onClick={onDelete} disabled={deleteMessage.isPending}>
                {deleteMessage.isPending ? <CircularProgress size={20} /> : <Delete />}
              </IconButton>
            ) : undefined
          }
        />
        <Stack spacing={2} px={2} pb={2}>
          <UnverifiedSenderWarning message={message} />

          {message.omittedAttachmentCount > 0 && (
            <Alert severity="warning">
              {message.omittedAttachmentCount} attachment{message.omittedAttachmentCount === 1 ? "" : "s"} omitted
              (attachments are not stored).
            </Alert>
          )}

          {message.publisherGameId && <FiledToSummary publisherGameId={message.publisherGameId} />}

          {isOpen && (
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <FileInboxMessageDialog message={message} onFiled={() => navigate(Path.inbox().none())} />
            </Stack>
          )}

          <Divider />

          <InboxMessageBody message={message} />
        </Stack>
      </Card>
    </>
  );
};
